import { useState } from 'react'
import {
  Accordion,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  usePlanoPmocDelete,
  usePlanoPmocDesvincularEquipamento,
  usePlanoPmocPersonalizarEquipamento,
  usePlanosPmocList,
} from '../../hooks/usePlanosPmoc'
import {
  CATEGORIAS_PLANO,
  PERIODICIDADES,
  TIPOS_PLANO,
  labelDe,
} from '../planos_modelo/constants'
import AplicarPlanoModeloModal from './AplicarPlanoModeloModal'
import AtividadePmocEditModal from './AtividadePmocEditModal'
import { extractApiError } from '../../lib/extractApiError'
import type { PMOC } from '../../types'
import type { AtividadePMOC, PlanoPMOC } from '../../types/pmocPlanos'

interface Props {
  pmoc: PMOC
}

export default function PmocPlanosTab({ pmoc }: Props) {
  const editavel = pmoc.status === 'rascunho'
  const lista = usePlanosPmocList(pmoc.id)
  const remove = usePlanoPmocDelete(pmoc.id)
  const desvincular = usePlanoPmocDesvincularEquipamento(pmoc.id)
  const personalizar = usePlanoPmocPersonalizarEquipamento(pmoc.id)
  const [aplicarAberto, setAplicarAberto] = useState(false)
  const [atividade, setAtividade] = useState<{
    atividade: AtividadePMOC
    planoPmocId: string
  } | null>(null)

  async function handleRemoverPlano(p: PlanoPMOC) {
    if (
      !confirm(
        `Remover plano "${p.nome}" deste PMOC? Esta ação NÃO afeta o PlanoModelo original — só este snapshot.`,
      )
    )
      return
    try {
      await remove.mutateAsync(p.id)
      notifications.show({ color: 'green', message: 'Snapshot removido do PMOC.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao remover.'),
      })
    }
  }

  async function handleDesvincular(p: PlanoPMOC, equipamentoId: string, nome: string) {
    if (!confirm(`Remover o plano "${p.nome}" somente de ${nome}?`)) return
    try {
      await desvincular.mutateAsync({ planoId: p.id, equipamentoId })
      notifications.show({
        color: 'green',
        message: 'Plano removido do equipamento. Agora você pode aplicar outro plano.',
      })
    } catch (err) {
      notifications.show({ color: 'red', message: extractApiError(err, 'Erro ao remover o plano.') })
    }
  }

  async function handlePersonalizar(p: PlanoPMOC, equipamentoId: string) {
    try {
      await personalizar.mutateAsync({ planoId: p.id, equipamentoId })
      notifications.show({
        color: 'green',
        message: 'Cópia exclusiva criada. As edições afetarão somente este equipamento.',
      })
    } catch (err) {
      notifications.show({ color: 'red', message: extractApiError(err, 'Erro ao personalizar o plano.') })
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={4}>Planos e atividades aplicadas ao PMOC</Title>
          <Text size="xs" c="dimmed">
            Snapshots de PlanoModelo aplicados a este PMOC. Edições aqui não
            afetam o catálogo original.
          </Text>
        </div>
        {editavel && (
          <Button
            onClick={() => setAplicarAberto(true)}
            data-testid="aplicar-plano-modelo-btn"
          >
            + Aplicar Plano Modelo
          </Button>
        )}
      </Group>

      {!editavel && (
        <Alert color="orange" variant="light">
          PMOC em status <strong>{pmoc.status}</strong> — planos e atividades não
          podem ser editados. Duplique o PMOC para criar nova versão rascunho.
        </Alert>
      )}

      {lista.isLoading ? (
        <Text c="dimmed">Carregando planos…</Text>
      ) : (lista.data?.results.length ?? 0) === 0 ? (
        <Paper p="lg" withBorder>
          <Text c="dimmed">
            Nenhum plano aplicado ainda. Use "+ Aplicar Plano Modelo" para
            adicionar planos do catálogo (
            <strong>/planos-modelo</strong>) a este PMOC.
          </Text>
        </Paper>
      ) : (
        <Accordion variant="separated" multiple>
          {lista.data?.results.map((p) => (
            <Accordion.Item key={p.id} value={p.id}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Group gap="xs" wrap="wrap">
                      <Text fw={600}>{p.nome}</Text>
                      <Badge variant="light">
                        {labelDe(p.tipo_plano, TIPOS_PLANO)}
                      </Badge>
                      <Badge variant="light" color="cyan">
                        {labelDe(p.categoria, CATEGORIAS_PLANO)}
                      </Badge>
                      <Badge variant="light" color="gray">
                        {p.atividades.length} atividades
                      </Badge>
                      {p.equipamentos.length > 0 && (
                        <Badge variant="light" color="green">
                          {p.equipamentos.length} equip.
                        </Badge>
                      )}
                      {!p.incluir_no_pdf && (
                        <Badge variant="light" color="orange">
                          fora do PDF
                        </Badge>
                      )}
                    </Group>
                    {p.descricao && (
                      <Text size="xs" c="dimmed" mt={2}>
                        {p.descricao}
                      </Text>
                    )}
                  </div>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="xs">
                  {p.equipamentos_detalhes.length > 0 && (
                    <Paper p="sm" withBorder bg="gray.0">
                      <Text fw={600} size="sm" mb="xs">Equipamentos vinculados</Text>
                      <Stack gap="xs">
                        {p.equipamentos_detalhes.map((eq) => {
                          const nome = [eq.marca, eq.modelo, eq.numero_serie && `S/N ${eq.numero_serie}`]
                            .filter(Boolean).join(' · ')
                          return (
                            <Group key={eq.id} justify="space-between" align="center">
                              <div>
                                <Text size="sm">{nome || eq.id}</Text>
                                {eq.area_nome && <Text size="xs" c="dimmed">Local: {eq.area_nome}</Text>}
                              </div>
                              {editavel && (
                                <Group gap="xs">
                                  <Button
                                    size="compact-xs"
                                    variant="light"
                                    loading={personalizar.isPending}
                                    onClick={() => handlePersonalizar(p, eq.id)}
                                  >
                                    Personalizar só este
                                  </Button>
                                  <Button
                                    size="compact-xs"
                                    color="red"
                                    variant="subtle"
                                    loading={desvincular.isPending}
                                    onClick={() => handleDesvincular(p, eq.id, nome || 'este equipamento')}
                                  >
                                    Remover deste equipamento
                                  </Button>
                                </Group>
                              )}
                            </Group>
                          )
                        })}
                      </Stack>
                    </Paper>
                  )}
                  {p.atividades.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      Nenhuma atividade neste plano.
                    </Text>
                  ) : (
                    p.atividades.map((at) => (
                      <Paper key={at.id} p="xs" withBorder>
                        <Group justify="space-between" wrap="nowrap">
                          <div style={{ flex: 1 }}>
                            <Group gap="xs" wrap="wrap">
                              <Text fw={600} size="sm">
                                {at.nome}
                              </Text>
                              <Badge size="sm" variant="light">
                                {labelDe(at.periodicidade, PERIODICIDADES)}
                                {at.periodicidade === 'customizada_dias' &&
                                at.periodicidade_customizada_dias
                                  ? ` (${at.periodicidade_customizada_dias}d)`
                                  : ''}
                              </Badge>
                              {at.exige_foto !== 'nunca' && (
                                <Badge size="sm" color="orange" variant="light">
                                  📷 Foto {at.exige_foto === 'obrigatoria' ? '(obrigatória)' : '(opcional)'}
                                </Badge>
                              )}
                              {at.exige_medicao && (
                                <Badge size="sm" color="cyan" variant="light">
                                  📊 {at.medicoes_esperadas.length} medições
                                </Badge>
                              )}
                              {!at.ativo && (
                                <Badge size="sm" color="gray">
                                  inativa
                                </Badge>
                              )}
                            </Group>
                            {at.criterio_esperado && (
                              <Text size="xs" c="dimmed" mt={2}>
                                <strong>Critério:</strong> {at.criterio_esperado}
                              </Text>
                            )}
                          </div>
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() =>
                              setAtividade({
                                atividade: at,
                                planoPmocId: p.id,
                              })
                            }
                          >
                            {editavel ? 'Editar' : 'Ver'}
                          </Button>
                        </Group>
                      </Paper>
                    ))
                  )}
                  {editavel && (
                    <Group justify="flex-end" mt="xs">
                      <Button
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={() => handleRemoverPlano(p)}
                      >
                        Remover snapshot deste PMOC
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      <AplicarPlanoModeloModal
        open={aplicarAberto}
        onClose={() => setAplicarAberto(false)}
        pmoc={pmoc}
        planosAplicados={lista.data?.results}
      />
      <AtividadePmocEditModal
        atividade={atividade?.atividade ?? null}
        planoPmocId={atividade?.planoPmocId ?? ''}
        readOnly={!editavel}
        onClose={() => setAtividade(null)}
      />
    </Stack>
  )
}
